import { stripInlineMarkdown } from 'shared/markdown/renderInlineMarkdown'

/**
 * How a parsed numeric markdown cell should be styled in ChartCard.
 *
 * - `number` — plain magnitude (`948`, `3,212`)
 * - `absolute_comparison` — signed delta without `%` (`+9,505,476.38`, `-12.9`)
 * - `percentage` — percent magnitude without a leading sign (`3.42%`)
 * - `percentage_comparison` — signed percent delta (`+3.42%`, `-2.64%`)
 */
export type MarkdownNumericCellStyle = 'number' | 'absolute_comparison' | 'percentage' | 'percentage_comparison'

const NUMERIC_CELL_PATTERN = /^[+-]?\d[\d,]*(?:\.\d+)?%?$/

const trimCell = (value: string): string => stripInlineMarkdown(value).trim()

export const classifyMarkdownNumericCell = (value: string): MarkdownNumericCellStyle | null => {
  const trimmed = trimCell(value)
  if (trimmed.length === 0) {
    return null
  }

  if (!NUMERIC_CELL_PATTERN.test(trimmed)) {
    return null
  }

  const isPercent = trimmed.endsWith('%')
  const isSigned = trimmed.startsWith('+') || trimmed.startsWith('-')

  if (isPercent && isSigned) {
    return 'percentage_comparison'
  }

  if (isPercent) {
    return 'percentage'
  }

  if (isSigned) {
    return 'absolute_comparison'
  }

  return 'number'
}

/**
 * Parses a numeric markdown cell. Percent values are returned as fractions
 * (e.g. `+3.42%` → `0.0342`) for ChartCard percentage metrics.
 */
export const parseMarkdownNumericCell = (value: string): number | null => {
  const trimmed = trimCell(value)
  if (trimmed.length === 0 || !NUMERIC_CELL_PATTERN.test(trimmed)) {
    return null
  }

  const isPercent = trimmed.endsWith('%')
  const normalized = trimmed.replaceAll(',', '').replace(/%$/, '')
  const numeric = Number(normalized)
  if (!Number.isFinite(numeric)) {
    return null
  }

  return isPercent ? numeric / 100 : numeric
}

/**
 * Infers a single style for a column from its cells.
 * Empty cells are ignored. Returns null if any non-empty cell is non-numeric.
 */
export const inferColumnNumericStyle = (values: string[]): MarkdownNumericCellStyle | null => {
  const styles: MarkdownNumericCellStyle[] = []

  for (const value of values) {
    if (trimCell(value).length === 0) {
      continue
    }

    const style = classifyMarkdownNumericCell(value)
    if (style == null) {
      return null
    }

    styles.push(style)
  }

  if (styles.length === 0) {
    return null
  }

  if (styles.some((style) => style === 'percentage_comparison')) {
    return 'percentage_comparison'
  }

  if (styles.some((style) => style === 'percentage')) {
    return 'percentage'
  }

  if (styles.some((style) => style === 'absolute_comparison')) {
    return 'absolute_comparison'
  }

  return 'number'
}
